import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { API, authFetch } from "@/App";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Bell, Heart, MessageCircle, UserPlus } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

export default function NotificationBell() {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000); // Poll every 30s
    return () => clearInterval(interval);
  }, []);

  const fetchUnreadCount = async () => {
    try {
      const res = await authFetch(`${API}/notifications/unread-count`);
      if (res.ok) {
        const data = await res.json();
        setUnreadCount(data.count);
      }
    } catch (error) {
      console.error("Failed to fetch unread count:", error);
    }
  };

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API}/notifications?limit=20`);
      if (res.ok) {
        const data = await res.json();
        setNotifications(data);
      }
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  const markAllRead = async () => {
    try {
      await authFetch(`${API}/notifications/mark-read`, { 
        method: "POST",
      });
      setUnreadCount(0);
      setNotifications(notifications.map(n => ({ ...n, read: true })));
    } catch (error) {
      console.error("Failed to mark as read:", error);
    }
  };

  const handleOpen = (isOpen) => {
    setOpen(isOpen);
    if (isOpen) {
      fetchNotifications();
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case "like":
        return <Heart className="w-4 h-4 text-pink-400 fill-pink-400" />;
      case "comment":
        return <MessageCircle className="w-4 h-4 text-blue-400" />;
      case "follow":
        return <UserPlus className="w-4 h-4 text-green-400" />;
      default:
        return <Bell className="w-4 h-4 text-purple-400" />;
    }
  };

  const getLink = (notif) => {
    if (notif.type === "follow") {
      return `/profile/${notif.actor_id}`;
    }
    if (notif.target_id) {
      return `/album/${notif.target_id.replace("log_", "")}`;
    }
    return "#";
  };

  return (
    <DropdownMenu open={open} onOpenChange={handleOpen}>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className="relative text-white/70 hover:text-white hover:bg-white/10"
          data-testid="notification-bell"
        >
          <Bell className="w-5 h-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-purple-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        align="end" 
        className="w-80 bg-[#1a1a2e] border-white/10 p-0"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <h3 className="font-semibold text-white">Notifications</h3>
          {unreadCount > 0 && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={markAllRead}
              className="text-xs text-purple-400 hover:text-purple-300 hover:bg-transparent"
            >
              Mark all read
            </Button>
          )}
        </div>

        {/* Notifications list */}
        <div className="max-h-96 overflow-y-auto">
          {loading ? (
            <div className="py-8 text-center text-white/50">Loading...</div>
          ) : notifications.length > 0 ? (
            notifications.map((notif) => (
              <Link
                key={notif.notification_id}
                to={getLink(notif)}
                className={`flex items-start gap-3 px-4 py-3 hover:bg-white/5 transition-colors ${
                  !notif.read ? "bg-purple-500/5" : ""
                }`}
                onClick={() => setOpen(false)}
              >
                <Avatar className="h-9 w-9 border border-white/10 flex-shrink-0">
                  <AvatarImage src={notif.actor_picture} alt={notif.actor_name} />
                  <AvatarFallback className="bg-purple-500/20 text-purple-400 text-sm">
                    {notif.actor_name?.charAt(0)?.toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white">
                    <span className="font-medium">{notif.actor_name}</span>{" "}
                    <span className="text-white/70">{notif.message}</span>
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    {getIcon(notif.type)}
                    <span className="text-xs text-white/40">
                      {formatDistanceToNow(new Date(notif.created_at), { addSuffix: true })}
                    </span>
                  </div>
                </div>
                {!notif.read && (
                  <div className="w-2 h-2 rounded-full bg-purple-500 flex-shrink-0 mt-2" />
                )}
              </Link>
            ))
          ) : (
            <div className="py-8 text-center">
              <Bell className="w-8 h-8 text-white/20 mx-auto mb-2" />
              <p className="text-sm text-white/50">No notifications yet</p>
            </div>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
